// three.js preview of the case: one mesh per part (`case`, `art`), orbit by drag. Adapted from apps/coaster/src/preview3d.js.
// The meshes arrive in the model's frame (spec D2): +x to the right LOOKING AT THE BACK, +y to the top, +z out of the back.
// That is three's own frame with the eye on +z looking at -z — so there is no rotation and no mirror anywhere here: a view
// is only a camera position. back = the eye on +z, front = the eye on -z, side = the eye on +x (the edge the Action button is on).
// The camera is fitted to the case's bounding box projected, and refitted at the current angle on every camera change.
import * as THREE from 'three';
const FILL = 0.94, EL_MAX = (85 * Math.PI) / 180;

// ---- the engraved art's colour (B1): a groove has no colour of its own, so the preview draws the art's outline on its floor in
// the case's colour shifted toward white when the case is dark and toward black when it is light, far enough that the two
// colours' contrast ratio (WCAG) reaches TINT_RATIO. The page promises 1.5; the target has room over it.
export const TINT_RATIO = 2, DARK_BELOW = 0.179;     // 0.179: where white and black are the same contrast away
const HEX = /^#[0-9a-fA-F]{6}$/;
const bytes = (hex) => [1, 3, 5].map((i) => parseInt((HEX.test(hex) ? hex : '#808080').slice(i, i + 2), 16));
const hexOf = (c) => '#' + c.map((v) => v.toString(16).padStart(2, '0')).join('').toUpperCase();
export const luminanceOf = (hex) => { const [r, g, b] = bytes(hex).map((v) => { v /= 255; return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4; }); return 0.2126 * r + 0.7152 * g + 0.0722 * b; };
export const contrastOf = (a, b) => { const x = luminanceOf(a), y = luminanceOf(b); return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05); };
export function artTint(hex) {
  const c = bytes(hex), end = luminanceOf(hex) < DARK_BELOW ? 255 : 0;
  for (let t = 0; t <= 1.0001; t += 0.01) {
    const out = hexOf(c.map((v) => Math.round(v + (end - v) * Math.min(t, 1))));
    if (contrastOf(hex, out) >= TINT_RATIO) return out;
  }
  return hexOf([end, end, end]);
}
// [azimuth about y from +z, elevation]: back 0, front half a turn, side a quarter turn
const VIEWS = { back: [0, 0], front: [Math.PI, 0], side: [Math.PI / 2, 0] };
export function createPreview(canvas, { onView } = {}) {
  // No multisampling (P1): with MSAA on, the inlay and the pocket walls, which lie 1.0 mm behind the back's inner face, showed through it
  // along the letters' edges in the front view (measured, 4 samples: 1092 stray pixels; 0 with MSAA off, the art hidden or moved 4 mm back).
  // Edges are smoothed by drawing at 2 to 3 device pixels per CSS pixel and letting the browser scale the canvas down.
  const renderer = new THREE.WebGLRenderer({ canvas, antialias: false, alpha: true });
  renderer.setPixelRatio(Math.min(Math.max(devicePixelRatio, 2), 3));
  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(35, 4 / 3, 1, 1000);
  let camR = 300, az = VIEWS.back[0], el = VIEWS.back[1];
  const placeCamera = () => {
    camera.position.set(camR * Math.cos(el) * Math.sin(az), camR * Math.sin(el), camR * Math.cos(el) * Math.cos(az));
    camera.lookAt(0, 0, 0);
  };
  // The key light rides on the camera, a few degrees off the view axis; a rough material keeps a Black case from washing grey.
  const key = new THREE.DirectionalLight(0xffffff, 1.6); key.position.set(-30, 40, 30); camera.add(key);
  const rim = new THREE.DirectionalLight(0xffffff, 1.0); rim.position.set(30, 10, -40); camera.add(rim);
  scene.add(new THREE.HemisphereLight(0xffffff, 0x8888aa, 1.6), camera);
  const group = new THREE.Group(); scene.add(group);
  const meshes = new Map();
  let tint = null;                                    // { case, art, ratio } of the engraved marker on show, else null
  const render = () => renderer.render(scene, camera);
  let corners = [];                                   // the union's bounding box corners, group-local
  function setScene(parts) {
    const geos = new Set();
    for (const c of [...group.children]) { group.remove(c); geos.add(c.geometry); c.material.dispose(); }
    for (const g of geos) g.dispose();
    meshes.clear(); tint = null;
    const bb = new THREE.Box3();
    const body = parts.find((q) => q.name === 'case');
    for (const p of parts) {
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(p.mesh.vertProperties), p.mesh.numProp));
      geo.setIndex(new THREE.BufferAttribute(new Uint32Array(p.mesh.triVerts), 1));
      geo.computeVertexNormals(); geo.computeBoundingBox();
      let color = p.color;
      if (p.preview) { color = artTint(body ? body.color : p.color); tint = { case: body ? body.color : p.color, art: color, ratio: contrastOf(body ? body.color : p.color, color) }; }
      const obj = new THREE.Mesh(geo, new THREE.MeshStandardMaterial({ color, roughness: 0.85, metalness: 0.05, flatShading: true }));
      bb.union(geo.boundingBox);
      group.add(obj);
      if (p.name) meshes.set(p.name, obj);
    }
    const c = bb.getCenter(new THREE.Vector3());
    corners = [];
    for (const x of [bb.min.x, bb.max.x]) for (const y of [bb.min.y, bb.max.y]) for (const z of [bb.min.z, bb.max.z]) corners.push(new THREE.Vector3(x, y, z));
    group.position.set(-c.x, -c.y, -c.z);             // the box's centre → the origin the camera looks at
    group.updateMatrixWorld(true);
    fit(); render();
  }
  const worldPoints = () => { group.updateMatrixWorld(true); return corners.map((p) => p.clone().applyMatrix4(group.matrixWorld)); };
  // Back the camera off until the projected box fills FILL of the canvas (width and height) at the CURRENT angle, then centre
  // its vertical extent on the canvas. A function of (scene, canvas, angle) only: call it after every angle change.
  function fit() {
    const r = canvas.getBoundingClientRect();
    if (!r.width || !r.height) return;
    camera.aspect = r.width / r.height;
    const pts = worldPoints();
    if (!pts.length) return;
    const size = new THREE.Box3().setFromPoints(pts).getSize(new THREE.Vector3());
    camR = Math.max(size.x, size.y, size.z, 10) * 3;
    const extents = () => {
      camera.clearViewOffset();
      camera.far = Math.max(1000, camR * 4); camera.updateProjectionMatrix();
      placeCamera(); camera.updateMatrixWorld(true);
      let mx = 0, lo = Infinity, hi = -Infinity;
      for (const p of pts) { const v = p.clone().project(camera); mx = Math.max(mx, Math.abs(v.x)); lo = Math.min(lo, v.y); hi = Math.max(hi, v.y); }
      return { mx, lo, hi };
    };
    for (let i = 0; i < 5; i++) { const e = extents(); camR *= Math.max(e.mx, (e.hi - e.lo) / 2) / FILL; }
    const e = extents();
    camera.setViewOffset(r.width, r.height, 0, (-((e.lo + e.hi) / 2) * r.height) / 2, r.width, r.height);
  }
  // → canvas pixels (relative to the canvas) of a world-space point
  const px = (v) => { const p = v.clone().project(camera); return [(p.x * 0.5 + 0.5) * canvas.clientWidth, (1 - (p.y * 0.5 + 0.5)) * canvas.clientHeight]; };
  function project(pts) {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const p of pts) { const [x, y] = px(p); minX = Math.min(minX, x); maxX = Math.max(maxX, x); minY = Math.min(minY, y); maxY = Math.max(maxY, y); }
    return { left: minX, top: minY, right: maxX, bottom: maxY, width: maxX - minX, height: maxY - minY };
  }
  // The case's box projected, as its bounding rectangle in canvas pixels.
  function sceneRect() {
    const pts = worldPoints();
    if (!pts.length) return null;
    camera.updateMatrixWorld(true);
    return project(pts);
  }
  // A part's bounding box projected to canvas pixels (relative to the canvas), null when it is not in the scene.
  function rectOf(name) {
    const m = meshes.get(name);
    if (!m) return null;
    group.updateMatrixWorld(true); camera.updateMatrixWorld(true);
    const b = new THREE.Box3().setFromObject(m), pts = [];
    for (const x of [b.min.x, b.max.x]) for (const y of [b.min.y, b.max.y]) for (const z of [b.min.z, b.max.z]) pts.push(new THREE.Vector3(x, y, z));
    return project(pts);
  }
  // A point of the model frame (D2) → where it is on the page, in CSS pixels. null with no scene. The test of "not mirrored".
  function screenOf([x, y, z]) {
    if (!meshes.size) return null;
    group.updateMatrixWorld(true); camera.updateMatrixWorld(true);
    const [cx, cy] = px(new THREE.Vector3(x, y, z).applyMatrix4(group.matrixWorld)), r = canvas.getBoundingClientRect();
    return { x: r.left + cx, y: r.top + cy };
  }
  function resize() {
    const w = canvas.clientWidth, h = canvas.clientHeight;
    if (!w || !h) return;
    renderer.setSize(w, h, false); fit(); render();
  }
  const names = Object.keys(VIEWS);
  function view() {
    // the azimuth is compared round the circle: a drag may have turned the camera a whole turn
    const turn = (a, b) => Math.abs(Math.atan2(Math.sin(a - b), Math.cos(a - b)));
    for (const n of names) if (turn(az, VIEWS[n][0]) < 1e-3 && Math.abs(el - VIEWS[n][1]) < 1e-3) return n;
    return null;
  }
  const changed = () => { if (onView) onView(view()); };
  function setView(name) {
    if (!Object.hasOwn(VIEWS, name)) return;
    [az, el] = VIEWS[name];
    fit(); render(); changed();
  }
  let dragging = false, lx = 0, ly = 0;
  canvas.addEventListener('pointerdown', (e) => { dragging = true; lx = e.clientX; ly = e.clientY; canvas.setPointerCapture(e.pointerId); });
  canvas.addEventListener('pointermove', (e) => {
    if (!dragging) return;
    az -= (e.clientX - lx) * 0.008; el = Math.min(EL_MAX, Math.max(-EL_MAX, el + (e.clientY - ly) * 0.006));
    lx = e.clientX; ly = e.clientY; fit(); render(); changed();
  });
  canvas.addEventListener('pointerup', () => { dragging = false; });
  new ResizeObserver(resize).observe(canvas); resize();
  return { setScene, tint: () => tint, setView, view, angles: () => ({ az, el }), refit: () => { fit(); render(); }, sceneRect, rectOf, screenOf, count: () => group.children.length };
}
